/**
 * AG Grid's scrolling element, and the header pinned inside the top of it.
 *
 * Verified against **AG Grid 36**. These are its own class names, so a major
 * upgrade is a reason to re-check them: nothing fails loudly if they change,
 * the overlay just stops knowing where its row is.
 */
const GRID_SCROLLER_SELECTOR = '.ag-grid-viewport'
const GRID_HEADER_SELECTOR = '.ag-header'

/**
 * The strip of the grid a row can actually be seen in.
 *
 * Not simply the scroller's box: the header is pinned **inside** the scroller
 * rather than sitting above it, so a row can slide under the header while
 * staying entirely unclipped. Anything reasoning about whether a row is visible
 * has to take the header's height off the top, or it will call a row hidden
 * behind the column titles wholly visible.
 *
 * Both lookups are AG Grid's own class names, which is the price of this being
 * about a grid row rather than an element in general. `null` when the row is not
 * inside a grid at all — every caller has a defined answer for that.
 */
export const getGridRowBand = (rowElement: HTMLElement) => {
  const scroller = rowElement.closest<HTMLElement>(GRID_SCROLLER_SELECTOR)
  if (!scroller) return null

  const header = scroller.querySelector(GRID_HEADER_SELECTOR)
  return {
    scroller,
    headerHeight: header?.getBoundingClientRect().height ?? 0,
  }
}

/**
 * Scrolls the grid the least it can to put a row wholly in view.
 *
 * The overlays anchored to a row only show while that row is whole, so opening
 * one on a half-scrolled row would otherwise do nothing a buyer can see — they
 * clicked, and the grid sat there. Moving the row into view instead makes the
 * click mean what it looks like it means.
 *
 * Scrolls the grid alone, by the exact shortfall: the page's own scroll position
 * is untouched, and a row already in view is left where it is rather than being
 * centred or nudged.
 */
export const scrollRowFullyIntoView = (rowElement: HTMLElement | null) => {
  if (!rowElement) return

  const band = getGridRowBand(rowElement)
  if (!band) return

  const row = rowElement.getBoundingClientRect()
  const scroller = band.scroller.getBoundingClientRect()

  /*
   * A row is always shorter than the strip it has to fit in, so at most one
   * edge can be over. Were that ever untrue, correcting the top would leave the
   * bottom still cut off.
   */
  const hiddenAbove = scroller.top + band.headerHeight - row.top
  const hiddenBelow = row.bottom - scroller.bottom

  if (hiddenAbove > 0) band.scroller.scrollTop -= hiddenAbove
  else if (hiddenBelow > 0) band.scroller.scrollTop += hiddenBelow
}
